import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { getReviews, createReview, updateReview, deleteReview } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    event_type: '',
    content: '',
    rating: 5,
    order: 0
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await getReviews();
      setReviews(res.data);
    } catch (error) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (review = null) => {
    if (review) {
      setFormData({
        name: review.name,
        event_type: review.event_type,
        content: review.content,
        rating: review.rating,
        order: review.order
      });
      setSelectedReview(review);
    } else {
      setFormData({
        name: '',
        event_type: '',
        content: '',
        rating: 5,
        order: reviews.length
      });
      setSelectedReview(null);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (selectedReview) {
        await updateReview(selectedReview.id, formData);
        toast.success('Review updated successfully');
      } else {
        await createReview(formData);
        toast.success('Review created successfully');
      }
      setDialogOpen(false);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to save review');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteReview(selectedReview.id);
      toast.success('Review deleted successfully');
      setDeleteDialogOpen(false);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-reviews-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Reviews
            </h1>
            <p className="text-neutral-500 mt-2">Manage client testimonials</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-neutral-900 hover:bg-neutral-800"
            data-testid="add-review-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Review
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="admin-card text-center py-16">
            <p className="text-neutral-500 mb-4">No reviews yet</p>
            <Button onClick={() => handleOpenDialog()}>Add your first review</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review) => (
              <div 
                key={review.id}
                className="admin-card"
                data-testid={`review-item-${review.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-200'}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleOpenDialog(review)}
                      data-testid={`edit-review-${review.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-red-600"
                      onClick={() => {
                        setSelectedReview(review);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-neutral-600 mb-4 line-clamp-3">"{review.content}"</p>
                <div className="border-t border-neutral-100 pt-4">
                  <p className="font-semibold">{review.name}</p>
                  <p className="text-sm text-neutral-500">{review.event_type}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedReview ? 'Edit Review' : 'Add Review'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Client Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., John & Sarah"
                    required
                    data-testid="review-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_type">Event Type</Label>
                  <Input
                    id="event_type"
                    value={formData.event_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_type: e.target.value }))}
                    placeholder="e.g., Wedding"
                    required
                    data-testid="review-event-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Rating</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                      className="p-1"
                      data-testid={`rating-star-${star}`}
                    >
                      <Star
                        size={24}
                        className={star <= formData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-200'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Review Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="What did the client say about your service?"
                  rows={4}
                  required
                  data-testid="review-content-input"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-neutral-900 hover:bg-neutral-800" data-testid="save-review-btn">
                  {selectedReview ? 'Update Review' : 'Add Review'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Review</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this review from {selectedReview?.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-review-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
